import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { BreadcrumbItem } from "@obg/core.breadcrumb";
import { CustomerSupportService } from "@obg/core.customer-support";
import { throttle } from "@obg/global";
import { DialogService } from "@obg/mat.ui.core";
import {
	AuthSelector,
	CORE_ROUTE,
	CoreUiInteractionEvent,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
	UiInteraction,
} from "@obg/ngx.core";
import {
	CoreContentState,
	Icon,
	IconSelector,
	ListOrientation,
	MenuItem,
	MenuSelector,
	TranslationService,
	UserStates,
} from "@obg/ngx.core.content";
import { DateFormat, DateService, DeviceConfigService, DevicePostfixPipe, DeviceType } from "@obg/ngx.global";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { FooterAction, TrackingContext } from "@obg/ngx.ui.core";
import { ElementSize } from "@obg/ngx.ui.global";
import { ignoreEmpty, ignoreNil } from "@obg/rxjs";
import { combineLatest, merge, Observable, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, take, tap } from "rxjs/operators";

import { FooterConfigService } from "./footer.config";
import { FooterBreakpoints, MegaMenuItem } from "./footer.model";

@Component({
	selector: "obg-footer-container",
	templateUrl: "./footer.container.html",
	styleUrls: ["./footer.container.scss", "./footer-home-page-v2.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class FooterContainer implements OnInit, OnDestroy {
	@HostBinding("class.home-page-v2") isHomePageV2Enabled = false;
	@HostBinding("class.obg-footer") hostCssClass = true;
	@HostBinding("class.obg-footer-small") isBreakpointSmall = false;
	@HostBinding("class.obg-footer-medium") isBreakpointMedium = false;
	@HostBinding("class.obg-footer-mobile") isBreakpointMobile = false;

	@Input()
	@coerceBoolean()
	hideSeo = false;

	@Input()
	@coerceBoolean()
	hideLanguageSelector = true;

	@Input()
	@coerceBoolean()
	hideExtendedLicencesSection = false;

	@Input()
	breadcrumbList: BreadcrumbItem[] | undefined;

	readonly leftSecurityIconKey = "footersecurity.left-image";
	readonly eepdLogoRegulationDesktopIconKey = "footer.regulation-eepd.logo.desktop";
	readonly eepdLogoRegulationMobileIconKey = "footer.regulation-eepd.logo.mobile";

	megaMenu$: Observable<MegaMenuItem[]> = this.getMenuItems$("desktopmegamenuheadings", 6).pipe(
		switchMap((headings) =>
			combineLatest(
				headings.map((heading) =>
					this.getMenuItems$(heading.key.toLowerCase(), 10).pipe(
						map((columnItems) => ({ ...heading, columnItems }))
					)
				)
			)
		)
	);

	isMobile = this.deviceConfigService.getType() === DeviceType.Mobile;
	isDesktop = this.deviceConfigService.getType() === DeviceType.Desktop;

	socialMediaIcons$ = this.getIcons$("footersocialnetworkicons");
	gameProviderIcons$ = this.getIcons$("footergameprovidericons");
	sponsorshipIcons$ = this.getIcons$("sponsorshipiconsfooter", this.config.getSponsorshipIconsLimit());
	paymentIcons$ = this.getIcons$("footerpaymenticons", 15);
	regulationIcons$ = combineLatest([
		this.getIconsWithExcludedKey$("footerregulationicons", this.getEepdLogoKey()),
		this.store
			.select(AuthSelector.isAuthenticated)
			.pipe(map((isAuth) => [UserStates.Anyone, isAuth ? UserStates.LoggedIn : UserStates.LoggedOut])),
	]).pipe(
		distinctUntilChanged(),
		map(([icons, allowedStates]) =>
			icons.filter((icon) => allowedStates.includes(icon.userState ?? UserStates.Anyone))
		)
	);
	eepdLogoIcon$ = this.getIcon$("footerregulationicons", this.getEepdLogoKey());
	awardIcons$ = this.getIcons$("footerawardicons", 12);
	securityLeftIcon$ = this.getIcon$("footersecurityicons", this.leftSecurityIconKey);
	footerMenu$ = this.getMenuItems$("footer-menu");
	otherLinksMenu$ = this.getMenuItems$("footer-other-links-menu");
	footerLicenses$ = this.getMenuItems$("footer-licenses-menu");
	isPaymentVisible = this.config.getPaymentsVisible();
	isGameProvidersVisible = this.config.getGameProvidersVisible();
	isResponsibleGamingVisible$ = this.config.getResponsibleGamingVisible$();
	isResponsibleGamingInfoEnabled = this.config.isResponsibleGamingInfoEnabled();
	isSponsorshipTitleEnabled = this.config.isFooterSponsorshipTitleEnabled();
	isSponsorshipIconEnabled = this.config.isFooterSponsorshipIconEnabled();
	isFooterAwardIconsEnabled = this.config.isFooterAwardIconsEnabled();
	isRegulatoryIconsFullSize = this.config.isRegulatoryIconsFullSize();
	isAams = false;
	isHgc = false;

	productInfoItemsOrientation = ListOrientation.Vertical;
	socialMediaTrackingData = FooterContainer.uiInteraction(FooterAction.SocialMedia);
	gameProviderTrackingData = FooterContainer.uiInteraction(FooterAction.GameProvider);
	sponsorsTrackingData = FooterContainer.uiInteraction(FooterAction.SponsorsPartnersAmbassadors);
	paymentTrackingData = FooterContainer.uiInteraction(FooterAction.PaymentsMethods);
	regulationTrackingData = FooterContainer.uiInteraction(FooterAction.Regulation);
	productLinksTrackingData = FooterContainer.uiInteraction(FooterAction.ProductLinks);
	responsibleGamingTrackingData = FooterContainer.uiInteraction(FooterAction.ResponsibleGamingLinks);
	awardTrackingData = FooterContainer.uiInteraction(FooterAction.Awards);
	licensesTrackingData = FooterContainer.uiInteraction(FooterAction.Licenses);
	otherClicksTrackingData = FooterContainer.uiInteraction(FooterAction.OtherClicks);
	currentYear = new Date().getFullYear();
	jurisdiction: Jurisdiction | undefined;

	isAuthenticated$ = this.store.select(AuthSelector.isAuthenticated);
	lastLoginMessage$ = this.getLastLoginMessage$();
	securityIcons$: Observable<Icon[]> | undefined;

	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreContentState>,
		private iconSelector: IconSelector,
		private config: FooterConfigService,
		private deviceConfigService: DeviceConfigService,
		private translationService: TranslationService,
		private devicePostfix: DevicePostfixPipe,
		private dateService: DateService,
		private customerSupportService: CustomerSupportService,
		private dialogService: DialogService
	) {
		this.securityIcons$ = combineLatest([
			this.getIconsWithExcludedKey$("footersecurityicons", this.leftSecurityIconKey),
			this.isAuthenticated$,
		]).pipe(
			distinctUntilChanged(),
			map(([icons, isAuth]) =>
				isAuth
					? icons.filter((icon) => icon.userState !== UserStates.LoggedOut)
					: icons.filter((icon) => icon.userState !== UserStates.LoggedIn)
			)
		);
	}

	private static uiInteraction(trackingData: string): UiInteraction {
		return {
			event: CoreUiInteractionEvent.Navigate,
			context: TrackingContext.FooterInteractionContext,
			data: {
				ItemClicked: trackingData,
			},
		};
	}

	ngOnInit() {
		const isHomePageV2Enabled = this.config.getHomePageV2Enabled();

		const homePageV2Enabled$$ = this.store.pipe(
			RouteSelector.getName$,
			filter((routeName) => routeName === CORE_ROUTE.home),
			filter(() => isHomePageV2Enabled),
			take(1),
			tap(() => {
				this.isHomePageV2Enabled = true;
				this.hideSeo = true;
			})
		);

		const jurisdiction$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			tap((jurisdiction) => {
				this.jurisdiction = jurisdiction;
				this.isHgc = jurisdiction === Jurisdiction.Hgc;
				this.isAams = jurisdiction === Jurisdiction.Aams;
			})
		);

		this.data$$ = merge(homePageV2Enabled$$, jurisdiction$).subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	trackByKey(_index: number, menuItem: MenuItem): string {
		return menuItem.key;
	}

	@throttle(100)
	onResize(elementSize: ElementSize) {
		const componentWidth = elementSize.width;
		this.isBreakpointSmall = componentWidth < FooterBreakpoints.Medium;
		this.isBreakpointMedium = componentWidth < FooterBreakpoints.Large && !this.isBreakpointSmall;
		this.isBreakpointMobile = this.isBreakpointSmall || this.isBreakpointMedium;
	}

	isIconListScaleEffectEnabled(jurisdiction: Jurisdiction): boolean {
		if (jurisdiction === "Ksa") {
			return false;
		}

		return true;
	}

	selectedItem(itemSelectedEventPayload: { item: MenuItem; sectionName: string }) {
		if (itemSelectedEventPayload.item.link?.target === "popup") {
			this.dialogService.openLinkInPopup(
				itemSelectedEventPayload.item.link!.url!,
				itemSelectedEventPayload.item.title
			);
		}
	}

	private getEepdLogoKey(): string {
		return this.isDesktop ? this.eepdLogoRegulationDesktopIconKey : this.eepdLogoRegulationMobileIconKey;
	}

	private getIconsByName$(name: string) {
		return this.store.pipe(select(this.iconSelector.get(name)), ignoreEmpty());
	}

	private getIcons$(name: string, amount?: number) {
		return this.getIconsByName$(name).pipe(map((icons) => (amount ? icons.slice(0, amount) : icons)));
	}

	private getIconsWithExcludedKey$(name: string, excludedKey: string) {
		return this.getIconsByName$(name).pipe(map((icons) => icons.filter((item) => item.key !== excludedKey)));
	}

	private getIcon$(name: string, key: string) {
		return this.getIconsByName$(name).pipe(map((icons) => icons.find((icon) => icon.key === key)));
	}

	private getMenu$(name: string) {
		return this.store.select(MenuSelector.get(name)).pipe(
			ignoreNil(),
			filter((menuItemGroup) => menuItemGroup.items.length > 0)
		);
	}

	private getMenuItems$(name: string, amount?: number) {
		return combineLatest([
			this.getMenu$(name).pipe(
				map((menuItemGroup) => {
					return menuItemGroup.items.slice(0, amount);
				})
			),
			this.customerSupportService.customerSupportUrl$,
		]).pipe(
			map(([items, customerSupportUrl]) => {
				return items.map((menuItem) => {
					if (menuItem?.link?.url && this.customerSupportService.isSupportURL(menuItem.link.url)) {
						const updatedUrl = new URL(menuItem.link.url);
						updatedUrl.hostname = new URL(customerSupportUrl).hostname;

						return {
							...menuItem,
							link: {
								...menuItem.link,
								url: updatedUrl.toString(),
							},
						};
					}
					return menuItem;
				});
			})
		);
	}

	private getLastLoginTimestamp$() {
		return this.store.pipe(
			select(CustomerManagementSelector.getCustomer),
			filter((customer) => !!customer),
			map((customer) => {
				if (!customer || customer.lastLogins.length < 2) {
					return undefined;
				}

				const lastLoginTimestamp = customer.lastLogins[1].timestamp;
				return {
					date: this.dateService.format(lastLoginTimestamp, DateFormat.ShortDate) as string,
					time: this.dateService.format(lastLoginTimestamp, DateFormat.ShortTime) as string,
				};
			})
		);
	}

	private getLastLoginMessage$() {
		return this.getLastLoginTimestamp$().pipe(
			map((timestamp) => {
				const crsKey = this.devicePostfix.transform("footer.lastlogin");
				return timestamp ? this.translationService.getByKey(crsKey, timestamp) : undefined;
			})
		);
	}
}
